import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { orgUsers, profiles, queues } from '../../data/offboarding'
import type {
  ModalIntent,
  ObjectType,
  OffboardingPreferences,
  OrgUser,
  OwnerMode,
  ReassignMode,
  ReassignmentSummary,
  ToastMessage,
} from '../../types/offboarding'

export const VALIDATION_COPY = {
  noObjects: 'Please select at least one object to reassign.',
  noSource: 'Please select at least one source owner.',
  noRecords: 'No records found for the selected source owners.',
  noTarget: 'Please configure a reassignment target.',
}

const STORAGE_KEY = 'activelink.offboarding.preferences'
const BATCH_THRESHOLD = 1000

const DEFAULT_PREFERENCES: OffboardingPreferences = {
  ownerMode: 'ALL_INACTIVE',
  sourceUserIds: [],
  objects: ['ACCOUNT'],
  reassignMode: 'USERS',
  targetUserIds: [],
  profileIds: [],
  accountQueueId: '',
  leadQueueId: '',
  excludedUserIds: [],
}

type Ledger = Record<ObjectType, string[]>

function sourceCandidatesFor(mode: OwnerMode): OrgUser[] {
  if (mode === 'PICK_ANY') return orgUsers.filter((u) => !u.excludedFromTargets)
  return orgUsers.filter((u) => !u.active && u.personAccounts + u.leads > 0)
}

function withSourceUser(
  prefs: OffboardingPreferences,
  userId?: string,
): OffboardingPreferences {
  if (!userId) return prefs
  return { ...prefs, ownerMode: 'PICK_ANY', sourceUserIds: [userId], objects: ['ACCOUNT', 'LEAD'] }
}

export function useOffboardingTool(initialSourceUserId?: string) {
  const [prefs, setPrefs] = useState<OffboardingPreferences>(() =>
    withSourceUser(DEFAULT_PREFERENCES, initialSourceUserId),
  )
  const [isInitializing, setIsInitializing] = useState(true)
  const [isExecuting, setIsExecuting] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [ledger, setLedger] = useState<Ledger>({ ACCOUNT: [], LEAD: [] })
  const [modalIntent, setModalIntent] = useState<ModalIntent | null>(null)
  const [toasts, setToasts] = useState<ToastMessage[]>([])
  const toastId = useRef(0)
  const hasMounted = useRef(false)

  // Initialize: load saved preferences
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY)
      if (saved) {
        setPrefs(
          withSourceUser({ ...DEFAULT_PREFERENCES, ...JSON.parse(saved) }, initialSourceUserId),
        )
      }
    } catch {
      // Ignore unreadable preferences and fall back to defaults
    }
    const timer = setTimeout(() => setIsInitializing(false), 700)
    return () => clearTimeout(timer)
  }, [])

  const pushToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = ++toastId.current
    setToasts((prev) => [...prev, { ...toast, id }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000)
  }, [])

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const update = (updates: Partial<OffboardingPreferences>) =>
    setPrefs((prev) => ({ ...prev, ...updates }))

  // ---- Source ----
  const remaining = useCallback(
    (u: OrgUser) => ({
      accounts: ledger.ACCOUNT.includes(u.id) ? 0 : u.personAccounts,
      leads: ledger.LEAD.includes(u.id) ? 0 : u.leads,
    }),
    [ledger],
  )

  const sourceCandidates = useMemo(
    () => sourceCandidatesFor(prefs.ownerMode),
    [prefs.ownerMode],
  )

  const sourceOwners = useMemo(() => {
    if (prefs.ownerMode === 'ALL_INACTIVE') {
      return orgUsers.filter((u) => {
        if (u.active) return false
        const r = remaining(u)
        return r.accounts + r.leads > 0
      })
    }
    return sourceCandidates.filter((u) => prefs.sourceUserIds.includes(u.id))
  }, [prefs.ownerMode, prefs.sourceUserIds, sourceCandidates, remaining])

  const counts = useMemo(
    () =>
      sourceOwners.reduce(
        (acc, u) => {
          const r = remaining(u)
          return { accounts: acc.accounts + r.accounts, leads: acc.leads + r.leads }
        },
        { accounts: 0, leads: 0 },
      ),
    [sourceOwners, remaining],
  )

  const includesAccounts = prefs.objects.includes('ACCOUNT')
  const includesLeads = prefs.objects.includes('LEAD')
  const selectedAccounts = includesAccounts ? counts.accounts : 0
  const selectedLeads = includesLeads ? counts.leads : 0
  const selectedTotal = selectedAccounts + selectedLeads

  // Preview refresh indicator — source changes only
  const sourceKey = `${prefs.ownerMode}|${prefs.sourceUserIds.join(',')}`
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    setIsRefreshing(true)
    const timer = setTimeout(() => setIsRefreshing(false), 350)
    return () => clearTimeout(timer)
  }, [sourceKey])

  // ---- Target ----
  const sourceIds = useMemo(() => new Set(sourceOwners.map((u) => u.id)), [sourceOwners])
  const targetCandidates = useMemo(
    () => orgUsers.filter((u) => u.active && !u.excludedFromTargets && !sourceIds.has(u.id)),
    [sourceIds],
  )
  const excludedIds = useMemo(() => new Set(prefs.excludedUserIds), [prefs.excludedUserIds])

  const recipients = useMemo(() => {
    if (prefs.reassignMode === 'USERS') {
      return targetCandidates.filter(
        (u) => prefs.targetUserIds.includes(u.id) && !excludedIds.has(u.id),
      )
    }
    if (prefs.reassignMode === 'USERS_BY_PROFILE') {
      return targetCandidates.filter(
        (u) => prefs.profileIds.includes(u.profileId) && !excludedIds.has(u.id),
      )
    }
    return []
  }, [prefs.reassignMode, prefs.targetUserIds, prefs.profileIds, targetCandidates, excludedIds])

  const profileMemberCounts = useMemo(
    () =>
      Object.fromEntries(
        profiles.map((p) => [
          p.id,
          orgUsers.filter((u) => u.active && !u.excludedFromTargets && u.profileId === p.id)
            .length,
        ]),
      ) as Record<string, number>,
    [],
  )

  const accountQueue = queues.find((q) => q.id === prefs.accountQueueId)
  const leadQueue = queues.find((q) => q.id === prefs.leadQueueId)

  const targetReady =
    prefs.reassignMode === 'QUEUE'
      ? (!includesAccounts || Boolean(accountQueue)) && (!includesLeads || Boolean(leadQueue))
      : recipients.length > 0

  const hasObjects = prefs.objects.length > 0
  const hasSource = sourceOwners.length > 0

  const blocker = !hasObjects
    ? VALIDATION_COPY.noObjects
    : !hasSource
      ? prefs.ownerMode === 'ALL_INACTIVE'
        ? VALIDATION_COPY.noRecords
        : VALIDATION_COPY.noSource
      : selectedTotal === 0
        ? VALIDATION_COPY.noRecords
        : !targetReady
          ? VALIDATION_COPY.noTarget
          : null

  const busy = isInitializing || isExecuting
  const canReassign = !blocker && !busy
  const canPreview = hasObjects && hasSource && selectedTotal > 0 && !busy

  // ---- Summary ----
  const summary: ReassignmentSummary = useMemo(() => {
    const sourceLines =
      prefs.ownerMode === 'ALL_INACTIVE'
        ? [`${sourceOwners.length} inactive owner${sourceOwners.length === 1 ? '' : 's'}`]
        : sourceOwners.map((u) => u.name)

    const objectLines = [
      ...(includesAccounts ? [{ label: 'Person Accounts', count: selectedAccounts }] : []),
      ...(includesLeads ? [{ label: 'Leads', count: selectedLeads }] : []),
    ]

    let targetLines: string[] = []
    if (prefs.reassignMode === 'USERS' && recipients.length) {
      targetLines = [
        `Even distribution to ${recipients.length} active user${recipients.length === 1 ? '' : 's'}`,
      ]
    } else if (prefs.reassignMode === 'USERS_BY_PROFILE' && recipients.length) {
      const names = profiles
        .filter((p) => prefs.profileIds.includes(p.id))
        .map((p) => `"${p.name}"`)
        .join(', ')
      targetLines = [
        `Even distribution to users with profile${prefs.profileIds.length === 1 ? '' : 's'} ${names} (~${recipients.length} users)`,
      ]
    } else if (prefs.reassignMode === 'QUEUE') {
      if (includesAccounts && accountQueue) targetLines.push(`Account Queue: ${accountQueue.name}`)
      if (includesLeads && leadQueue) targetLines.push(`Lead Queue: ${leadQueue.name}`)
      if (!targetReady) targetLines = []
    }

    return {
      sourceLines,
      objectLines,
      targetLines,
      recipientCount: recipients.length,
      perRecipient: recipients.length ? Math.ceil(selectedTotal / recipients.length) : 0,
      excludedCount: prefs.excludedUserIds.length,
    }
  }, [
    prefs,
    sourceOwners,
    includesAccounts,
    includesLeads,
    selectedAccounts,
    selectedLeads,
    selectedTotal,
    recipients,
    accountQueue,
    leadQueue,
    targetReady,
  ])

  // ---- Actions ----
  const setOwnerMode = (mode: OwnerMode) => {
    const allowed = new Set(sourceCandidatesFor(mode).map((u) => u.id))
    update({ ownerMode: mode, sourceUserIds: prefs.sourceUserIds.filter((id) => allowed.has(id)) })
  }

  const toggleObject = (object: ObjectType) => {
    update({
      objects: prefs.objects.includes(object)
        ? prefs.objects.filter((o) => o !== object)
        : [...prefs.objects, object],
    })
  }

  const savePreferences = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
      pushToast({ title: 'Success', message: 'Preferences saved successfully.', variant: 'success' })
    } catch {
      pushToast({ title: 'Error', message: 'Preferences could not be saved.', variant: 'error' })
    }
  }

  const openModal = (intent: ModalIntent) => {
    if (intent === 'reassign' && blocker) {
      pushToast({ title: 'Validation Error', message: blocker, variant: 'error' })
      return
    }
    setModalIntent(intent)
  }

  const closeModal = useCallback(() => setModalIntent(null), [])

  const confirmReassign = () => {
    if (!canReassign) return
    const ownerIds = sourceOwners.map((u) => u.id)
    const objects = [...prefs.objects]
    const accounts = selectedAccounts
    const leads = selectedLeads
    setModalIntent(null)
    setIsExecuting(true)
    setTimeout(() => {
      setLedger((prev) => ({
        ACCOUNT: objects.includes('ACCOUNT') ? [...prev.ACCOUNT, ...ownerIds] : prev.ACCOUNT,
        LEAD: objects.includes('LEAD') ? [...prev.LEAD, ...ownerIds] : prev.LEAD,
      }))
      setIsExecuting(false)
      if (accounts > BATCH_THRESHOLD || leads > BATCH_THRESHOLD) {
        pushToast({
          title: 'Success',
          message: 'Batch job started. You will receive a summary email when complete.',
          variant: 'success',
        })
      } else {
        const parts = [
          ...(objects.includes('ACCOUNT') ? [`Accounts: ${accounts}/${accounts}`] : []),
          ...(objects.includes('LEAD') ? [`Leads: ${leads}/${leads}`] : []),
        ]
        pushToast({ title: 'Success', message: `Reassigned — ${parts.join(', ')}`, variant: 'success' })
      }
    }, 1800)
  }

  const previewOnly = () => {
    setModalIntent(null)
    const parts = [
      ...(includesAccounts ? [`Accounts: ${selectedAccounts}`] : []),
      ...(includesLeads ? [`Leads: ${selectedLeads}`] : []),
    ]
    pushToast({
      title: 'Preview complete',
      message: `${parts.join(', ')} would be reassigned. No records were changed.`,
      variant: 'success',
    })
  }

  return {
    prefs,
    isInitializing,
    isExecuting,
    isRefreshing,
    busy,
    sourceCandidates,
    sourceOwners,
    counts,
    selectedTotal,
    targetCandidates,
    recipients,
    profileMemberCounts,
    blocker,
    canReassign,
    canPreview,
    summary,
    modalIntent,
    toasts,
    setOwnerMode,
    setSourceUserIds: (ids: string[]) => update({ sourceUserIds: ids }),
    toggleObject,
    setReassignMode: (mode: ReassignMode) => update({ reassignMode: mode }),
    setTargetUserIds: (ids: string[]) => update({ targetUserIds: ids }),
    setProfileIds: (ids: string[]) => update({ profileIds: ids }),
    setAccountQueueId: (id: string) => update({ accountQueueId: id }),
    setLeadQueueId: (id: string) => update({ leadQueueId: id }),
    setExcludedUserIds: (ids: string[]) => update({ excludedUserIds: ids }),
    savePreferences,
    openModal,
    closeModal,
    confirmReassign,
    previewOnly,
    dismissToast,
  }
}
